import { prisma, OrderStatus, OrderItemStatus, Prisma } from "client";
import {
  writeCache,
  readCache,
  deleteCache,
  getOrSetCacheWithHit,
} from "@sajilni/cache";
import {
  CHECKIN_ROSTER_TTL_SECONDS,
  CHECKIN_RULES_TTL_SECONDS,
  checkinRosterKey,
  checkinRulesKey,
  checkinWarmMetaKey,
} from "@sajilni/config/constants";
import { createCounter } from "@sajilni/observability/metrics";

const LOG_PREFIX = "[checkinCache]";

const checkinCacheHits = createCounter(
  "checkin.cache.hits",
  "Check-in cache hits",
  "1"
);
const checkinCacheMisses = createCounter(
  "checkin.cache.misses",
  "Check-in cache misses",
  "1"
);

export class CheckinWarmEventNotFoundError extends Error {
  constructor(public readonly eventId: string) {
    super(`Event not found: ${eventId}`);
    this.name = "CheckinWarmEventNotFoundError";
  }
}

export type WarmCheckinCacheResult = {
  warmed: number;
  failed: number;
  totalOrderItems: number;
  elapsedMs: number;
};

export type CheckinWarmMeta = {
  warmedAt: string;
  warmed: number;
  failed: number;
  totalOrderItems: number;
  elapsedMs: number;
};

/**
 * Static-per-event roster entry pre-warmed into Redis before doors open.
 *
 * Keyed by `orderItemId` only (not by eventId): the scan path receives
 * `orderItemId` from the QR payload but does NOT know `eventId` until
 * it fetches the order — so eventId is stored *inside* the entry instead.
 */
export type CheckinRosterEntry = {
  orderItemId: string;
  eventId: string;
  ticketId: string | null;
  bookableItemId: string | null;
  invitationId: string | null;
  participantGroupIds: string[];
  eventEndDateIso: string | null;
  eventTimezone: string | null;
} & Record<string, unknown>;

const WARM_BATCH_SIZE = 200;

const rosterItemSelect = {
  id: true,
  ticket: {
    select: { id: true, bookableItem: { select: { id: true } } },
  },
  invitationAttendee: {
    select: { invitation: { select: { id: true } } },
  },
  order: {
    select: {
      eventId: true,
      profile: {
        select: { participantGroups: { select: { id: true } } },
      },
      event: { select: { id: true, endDate: true, timezone: true } },
    },
  },
} satisfies Prisma.OrderItemSelect;

type RosterOrderItem = Prisma.OrderItemGetPayload<{
  select: typeof rosterItemSelect;
}>;

function buildRosterEntry(
  item: RosterOrderItem,
  eventId: string
): CheckinRosterEntry {
  return {
    orderItemId: item.id,
    eventId,
    ticketId: item.ticket?.id ?? null,
    bookableItemId: item.ticket?.bookableItem?.id ?? null,
    invitationId: item.invitationAttendee?.invitation?.id ?? null,
    participantGroupIds:
      item.order?.profile?.participantGroups?.map((g) => g.id) ?? [],
    eventEndDateIso: item.order?.event?.endDate?.toISOString() ?? null,
    eventTimezone: item.order?.event?.timezone ?? null,
  };
}

function zoneCheckInRulesInclude(zoneId: string) {
  return {
    zones: {
      where: { id: zoneId },
      include: {
        accessibleInvitations: { select: { id: true } },
        accessibleUserGroups: { select: { id: true } },
        accessibleBookableItems: { select: { id: true } },
      },
    },
  } satisfies Prisma.CheckInRulesInclude;
}

async function fetchCheckInRulesForZoneFromDb(zoneId: string) {
  return prisma.checkInRules.findMany({
    where: { zonesIds: { has: zoneId }, deletedAt: null },
    include: zoneCheckInRulesInclude(zoneId),
  });
}

export type CheckInRulesForZone = Awaited<
  ReturnType<typeof fetchCheckInRulesForZoneFromDb>
>;

/**
 * Cache-first zone rules read with fail-open semantics.
 * If Redis is down or times out, falls back to MongoDB so scans keep working.
 */
export async function readCheckinRulesForZone(
  zoneId: string
): Promise<CheckInRulesForZone> {
  const key = checkinRulesKey(zoneId);
  try {
    const { data: rules, hit } = await getOrSetCacheWithHit(
      key,
      () => fetchCheckInRulesForZoneFromDb(zoneId),
      { ttlSeconds: CHECKIN_RULES_TTL_SECONDS }
    );
    if (hit) checkinCacheHits.add(1, { scope: "rules" });
    else checkinCacheMisses.add(1, { scope: "rules" });
    return rules;
  } catch (err) {
    console.warn(
      `${LOG_PREFIX} rules READ_ERROR zoneId=${zoneId} key=${key} — falling back to DB err=${err instanceof Error ? err.message : String(err)}`
    );
    return fetchCheckInRulesForZoneFromDb(zoneId);
  }
}

export async function readCheckinWarmMeta(
  eventId: string
): Promise<CheckinWarmMeta | null> {
  try {
    return await readCache<CheckinWarmMeta>(checkinWarmMetaKey(eventId));
  } catch (err) {
    console.warn(
      `${LOG_PREFIX} warmMeta READ_ERROR eventId=${eventId} err=${err instanceof Error ? err.message : String(err)}`
    );
    return null;
  }
}

async function recordCheckinWarmMeta(
  eventId: string,
  result: WarmCheckinCacheResult
): Promise<void> {
  try {
    await writeCache(
      checkinWarmMetaKey(eventId),
      {
        warmedAt: new Date().toISOString(),
        warmed: result.warmed,
        failed: result.failed,
        totalOrderItems: result.totalOrderItems,
        elapsedMs: result.elapsedMs,
      },
      { ttlSeconds: CHECKIN_ROSTER_TTL_SECONDS }
    );
  } catch (err) {
    console.warn(
      `${LOG_PREFIX} warmMeta WRITE_ERROR eventId=${eventId} err=${err instanceof Error ? err.message : String(err)}`
    );
  }
}

async function assertEventExistsForWarm(eventId: string): Promise<void> {
  const event = await prisma.event.findFirst({
    where: {
      id: eventId,
      OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
    },
    select: { id: true },
  });
  if (!event) {
    throw new CheckinWarmEventNotFoundError(eventId);
  }
}

export async function warmCheckinCache(
  eventId: string
): Promise<WarmCheckinCacheResult> {
  const startedAt = Date.now();
  console.log(`${LOG_PREFIX} warmCheckinCache START eventId=${eventId}`);

  await assertEventExistsForWarm(eventId);

  const orders = await prisma.order.findMany({
    where: {
      eventId,
      status: OrderStatus.COMPLETED,
      OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
    },
    select: { id: true },
  });

  if (orders.length === 0) {
    const elapsedMs = Date.now() - startedAt;
    const result: WarmCheckinCacheResult = {
      warmed: 0,
      failed: 0,
      totalOrderItems: 0,
      elapsedMs,
    };
    console.log(
      `${LOG_PREFIX} warmCheckinCache SKIP eventId=${eventId} reason=no_completed_orders elapsedMs=${elapsedMs}`
    );
    await recordCheckinWarmMeta(eventId, result);
    return result;
  }

  const orderIds = orders.map((o) => o.id);
  const orderItems = await prisma.orderItem.findMany({
    where: {
      orderId: { in: orderIds },
      status: OrderItemStatus.CONFIRMED,
    },
    select: rosterItemSelect,
  });

  console.log(
    `${LOG_PREFIX} warmCheckinCache items eventId=${eventId} confirmedOrderItems=${orderItems.length}`
  );

  let warmed = 0;
  let failed = 0;
  const batchCount = Math.ceil(orderItems.length / WARM_BATCH_SIZE);

  for (let i = 0; i < orderItems.length; i += WARM_BATCH_SIZE) {
    const batchIndex = Math.floor(i / WARM_BATCH_SIZE) + 1;
    const chunk = orderItems.slice(i, i + WARM_BATCH_SIZE);
    const batchStart = Date.now();

    const results = await Promise.allSettled(
      chunk.map((item) =>
        writeCache(checkinRosterKey(item.id), buildRosterEntry(item, eventId), {
          ttlSeconds: CHECKIN_ROSTER_TTL_SECONDS,
        })
      )
    );

    const batchWarmed = results.filter((r) => r.status === "fulfilled").length;
    const batchFailed = results.filter((r) => r.status === "rejected").length;
    warmed += batchWarmed;
    failed += batchFailed;

    if (batchFailed > 0) {
      const firstError = results.find((r) => r.status === "rejected") as
        | PromiseRejectedResult
        | undefined;
      console.warn(
        `${LOG_PREFIX} warmCheckinCache batch PARTIAL eventId=${eventId} batch=${batchIndex}/${batchCount} warmed=${batchWarmed} failed=${batchFailed} elapsedMs=${Date.now() - batchStart} err=${firstError?.reason instanceof Error ? firstError.reason.message : String(firstError?.reason)}`
      );
    }
  }

  const elapsedMs = Date.now() - startedAt;
  const result: WarmCheckinCacheResult = {
    warmed,
    failed,
    totalOrderItems: orderItems.length,
    elapsedMs,
  };
  const summary = `${LOG_PREFIX} warmCheckinCache DONE eventId=${eventId} warmed=${warmed} failed=${failed} total=${orderItems.length} elapsedMs=${elapsedMs}`;
  if (failed > 0) console.warn(summary);
  else console.log(summary);

  await recordCheckinWarmMeta(eventId, result);
  return result;
}

/** Best-effort warm for one confirmed order item (late registration path). */
export async function warmCheckinRosterEntry(
  orderItemId: string
): Promise<boolean> {
  try {
    const item = await prisma.orderItem.findUnique({
      where: { id: orderItemId },
      select: rosterItemSelect,
    });
    if (!item || item.order?.event?.id == null) return false;

    const eventId = item.order.event.id;
    await writeCache(
      checkinRosterKey(orderItemId),
      buildRosterEntry(item, eventId),
      { ttlSeconds: CHECKIN_ROSTER_TTL_SECONDS }
    );
    return true;
  } catch (err) {
    console.warn(
      `${LOG_PREFIX} roster WARM_FAIL orderItemId=${orderItemId} err=${err instanceof Error ? err.message : String(err)}`
    );
    return false;
  }
}

/** Warm all confirmed items on a completed order (fire-and-forget from order flow). */
export async function warmCheckinRosterEntriesForOrder(
  orderId: string
): Promise<void> {
  const items = await prisma.orderItem.findMany({
    where: { orderId, status: OrderItemStatus.CONFIRMED },
    select: { id: true },
  });
  if (items.length === 0) return;

  await Promise.allSettled(
    items.map((item) => warmCheckinRosterEntry(item.id))
  );
}

export async function readCheckinRosterEntry(
  orderItemId: string
): Promise<CheckinRosterEntry | null> {
  const key = checkinRosterKey(orderItemId);
  try {
    const entry = await readCache<CheckinRosterEntry>(key);
    if (entry) {
      checkinCacheHits.add(1, { scope: "roster" });
      return entry;
    }
    checkinCacheMisses.add(1, { scope: "roster" });
    return null;
  } catch (err) {
    console.warn(
      `${LOG_PREFIX} roster READ_ERROR orderItemId=${orderItemId} key=${key} err=${err instanceof Error ? err.message : String(err)}`
    );
    return null;
  }
}

export async function invalidateCheckinRosterEntry(
  orderItemId: string
): Promise<void> {
  const key = checkinRosterKey(orderItemId);
  try {
    await deleteCache(key);
  } catch (err) {
    console.warn(
      `${LOG_PREFIX} roster INVALIDATE_ERROR orderItemId=${orderItemId} err=${err instanceof Error ? err.message : String(err)}`
    );
  }
}

export async function invalidateCheckinRosterForOrder(
  orderId: string
): Promise<void> {
  const items = await prisma.orderItem.findMany({
    where: { orderId },
    select: { id: true },
  });
  if (items.length === 0) return;

  await Promise.allSettled(
    items.map((item) => invalidateCheckinRosterEntry(item.id))
  );
  console.log(
    `${LOG_PREFIX} roster INVALIDATE_ORDER orderId=${orderId} items=${items.length}`
  );
}

export async function invalidateCheckinRosterForProfile(
  profileId: string
): Promise<void> {
  const items = await prisma.orderItem.findMany({
    where: { order: { profileId } },
    select: { id: true },
  });
  if (items.length === 0) return;

  await Promise.allSettled(
    items.map((item) => invalidateCheckinRosterEntry(item.id))
  );
  console.log(
    `${LOG_PREFIX} roster INVALIDATE_PROFILE profileId=${profileId} items=${items.length}`
  );
}
